import React from 'react';
import { Card, Descriptions, Tag, Typography, Space, Divider } from 'antd';
import { 
  CheckCircleOutlined, 
  SyncOutlined, 
  ClockCircleOutlined,
  AmazonOutlined,
  ShopOutlined,
  CalendarOutlined,
  TruckOutlined,
  CreditCardOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

const OrderInfo = ({ order }) => {
  const getOrderStatusDisplay = (status) => {
    let color = '';
    let icon = null;
    let text = '';
    
    switch(status) {
      case 'Shipped':
        color = 'green';
        icon = <CheckCircleOutlined />;
        text = 'Shipped';
        break;
      case 'PartiallyShipped':
        color = 'blue';
        icon = <SyncOutlined />;
        text = 'Partially Shipped';
        break;
      case 'Unshipped':
        color = 'orange';
        icon = <ClockCircleOutlined />;
        text = 'Unshipped';
        break;
      case 'Canceled':
        color = 'red';
        icon = <ClockCircleOutlined />;
        text = 'Canceled';
        break;
      default:
        color = 'default';
        icon = <ClockCircleOutlined />;
        text = status || 'Unknown';
    }
    
    return <Tag icon={icon} color={color}>{text}</Tag>;
  };

  const getFulfillmentChannelDisplay = (channel) => {
    return channel === 'AFN' ? (
      <Tag color="orange" icon={<AmazonOutlined />}>AFN (Amazon Fulfillment)</Tag>
    ) : (
      <Tag color="blue" icon={<ShopOutlined />}>MFN (Merchant Fulfillment)</Tag>
    );
  };

  const getShipServiceDisplay = (service) => {
    return service === 'Expedited' ? (
      <Tag color="blue">🚀 Expedited</Tag>
    ) : (
      <Tag color="default">📦 Standard</Tag>
    );
  };

  return (
    <Card 
      title={
        <Space>
          <AmazonOutlined style={{ color: '#FF9900' }} />
          <span>Order Information</span>
        </Space>
      } 
      style={{ marginBottom: 16 }} 
      className="order-info-card"
    >
      <Descriptions 
        column={1} 
        size="small" 
        colon={false}
        labelStyle={{ width: '140px', textAlign: 'left' }}
        contentStyle={{ textAlign: 'left' }}
      >
        <Descriptions.Item 
          label={
            <Space>
              <AmazonOutlined style={{ fontSize: '12px' }} />
              <span>Amazon Order ID</span>
            </Space>
          }
        >
          <Text 
            strong 
            style={{ fontFamily: 'monospace' }}
            copyable={{ text: order.amazonOrderId }}
          >
            {order.amazonOrderId}
          </Text>
        </Descriptions.Item>

        <Descriptions.Item 
          label={
            <Space>
              <ShopOutlined style={{ fontSize: '12px' }} />
              <span>Seller Account</span>
            </Space>
          }
        >
          <Text>{order.sellerAccount?.name || 'N/A'}</Text>
          {order.sellerAccount?.sellerId && (
            <div>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                ID: {order.sellerAccount.sellerId}
              </Text>
            </div>
          )}
        </Descriptions.Item>

        <Descriptions.Item 
          label={
            <Space>
              <GlobalOutlined style={{ fontSize: '12px' }} />
              <span>Marketplace</span>
            </Space>
          }
        >
          <Text>{order.marketplace || 'N/A'}</Text>
        </Descriptions.Item>

        <Divider style={{ margin: '12px 0' }} />

        <Descriptions.Item 
          label={
            <Space>
              <CalendarOutlined style={{ fontSize: '12px' }} />
              <span>Purchase Date</span>
            </Space>
          }
        >
          <Text>
            {order.purchaseDate ? dayjs(order.purchaseDate).format('MMM DD, YYYY HH:mm') : 'N/A'}
          </Text>
          {order.purchaseDate && (
            <div>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {dayjs(order.purchaseDate).format('dddd')}
              </Text>
            </div>
          )}
        </Descriptions.Item>

        <Descriptions.Item 
          label={
            <Space>
              <CalendarOutlined style={{ fontSize: '12px' }} />
              <span>Last Update</span>
            </Space>
          }
        >
          <Text>
            {order.lastUpdateDate ? dayjs(order.lastUpdateDate).format('MMM DD, YYYY HH:mm') : 'N/A'}
          </Text>
          {order.lastUpdateDate && (
            <div>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {dayjs(order.lastUpdateDate).format('dddd')}
              </Text>
            </div>
          )}
        </Descriptions.Item>

        <Divider style={{ margin: '12px 0' }} />

        <Descriptions.Item label="Order Status">
          {getOrderStatusDisplay(order.orderStatus)}
        </Descriptions.Item>

        <Descriptions.Item label="Fulfillment Channel">
          {getFulfillmentChannelDisplay(order.fulfillmentChannel)}
        </Descriptions.Item>

        <Descriptions.Item label="Ship Service Level">
          {getShipServiceDisplay(order.shipServiceLevel)}
        </Descriptions.Item>

        <Divider style={{ margin: '12px 0' }} />

        {order.earliestShipDate && (
          <Descriptions.Item 
            label={
              <Space>
                <TruckOutlined style={{ fontSize: '12px' }} />
                <span>Earliest Ship Date</span>
              </Space>
            }
          >
            <Text>{dayjs(order.earliestShipDate).format('MMM DD, YYYY')}</Text>
          </Descriptions.Item>
        )}

        {order.earliestDeliveryDate && (
          <Descriptions.Item 
            label={
              <Space>
                <TruckOutlined style={{ fontSize: '12px' }} />
                <span>Earliest Delivery Date</span>
              </Space>
            }
          >
            <Text>{dayjs(order.earliestDeliveryDate).format('MMM DD, YYYY')}</Text>
          </Descriptions.Item>
        )}

        {order.paymentMethod && (
          <Descriptions.Item 
            label={
              <Space>
                <CreditCardOutlined style={{ fontSize: '12px' }} />
                <span>Payment Method</span>
              </Space>
            }
          >
            <Text>{order.paymentMethod}</Text>
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

export default OrderInfo; 